import { useCallback, useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAdminFetch } from "@/hooks/use-admin-fetch";

/**
 * Admin → DSA reports moderation queue (Digital Services Act Article 16-17).
 *
 * Pairs with apps/api/src/routes/admin-dsa-reports.ts. Surface mirrors the
 * established admin pattern (claims, feedback): paginated list + filter
 * chips + side-sheet detail with resolve / escalate / reject actions.
 *
 * Wiring (apps/api endpoints — confirmed live):
 *   - GET    /api/admin/dsa-reports?status=...&category=...&q=...&from=...&to=...&limit=...
 *   - GET    /api/admin/dsa-reports/counts                → status histogram
 *   - GET    /api/admin/dsa-reports/:id                   → full detail
 *   - POST   /api/admin/dsa-reports/:id/resolve           → SoR + decision
 *   - POST   /api/admin/dsa-reports/:id/escalate          → in_review
 *   - PATCH  /api/admin/dsa-reports/:id { status }        → quick status set
 */

interface ReporterRef {
  id: string;
  email: string | null;
}

interface DsaReportRow {
  id: string;
  reporterId: string | null;
  reporterEmail: string | null;
  reporterName: string | null;
  contentType: string;
  contentId: string | null;
  contentUrl: string | null;
  category: string;
  reason: string;
  status: string;
  actionTaken: string | null;
  decision: string | null;
  decidedAt: string | null;
  reporterNotifiedAt: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  language: string | null;
  createdAt: string;
  reporter: ReporterRef | null;
  handler: ReporterRef | null;
}

interface ListResponse {
  reports: DsaReportRow[];
  total: number;
  limit: number;
  offset: number;
}

const STATUS_FILTERS = [
  "all",
  "pending",
  "in_review",
  "action_taken",
  "no_action",
  "rejected",
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number];

const CATEGORY_OPTIONS = [
  "illegal_content",
  "defamation",
  "copyright",
  "fake",
  "harassment",
  "hate_speech",
  "medical_misinfo",
  "other",
] as const;

const DECISION_OPTIONS = ["removed", "kept", "modified", "restricted", "warning"] as const;

type Decision = (typeof DECISION_OPTIONS)[number];

const statusStyles: Record<string, string> = {
  pending: "border-amber-300 bg-amber-50 text-amber-700",
  in_review: "border-purple-300 bg-purple-50 text-purple-700",
  action_taken: "border-emerald-300 bg-emerald-50 text-emerald-700",
  no_action: "border-gray-300 bg-gray-50 text-gray-700",
  rejected: "border-red-300 bg-red-50 text-red-700",
};

const PAGE_SIZE = 50;

export const Route = createFileRoute("/_authenticated/dsa-reports")({
  component: DsaReportsPage,
});

function DsaReportsPage() {
  const { t } = useLingui();
  const { adminFetch } = useAdminFetch();

  const [filter, setFilter] = useState<StatusFilter>("pending");
  const [category, setCategory] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const [page, setPage] = useState(0);

  const [list, setList] = useState<DsaReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  const refreshCounts = useCallback(async () => {
    try {
      const data = await adminFetch.get<Record<string, number>>("/api/admin/dsa-reports/counts");
      setCounts(data);
    } catch {
      /* ignore — counts are advisory */
    }
  }, [adminFetch]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("status", filter);
      if (category) params.set("category", category);
      if (q.trim()) params.set("q", q.trim());
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(page * PAGE_SIZE));

      const data = await adminFetch.get<ListResponse>(
        `/api/admin/dsa-reports?${params.toString()}`,
      );
      setList(data.reports);
      setTotal(data.total);
    } catch {
      setList([]);
      setTotal(0);
    }
    setLoading(false);
  }, [adminFetch, filter, category, q, page]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  // Reset page when filters change.
  useEffect(() => {
    setPage(0);
  }, [filter, category, q]);

  const onResolved = useCallback(() => {
    fetchList();
    refreshCounts();
    setOpenId(null);
  }, [fetchList, refreshCounts]);

  const filterLabels = useMemo<Record<StatusFilter, string>>(
    () => ({
      all: t`All`,
      pending: t`Pending`,
      in_review: t`In review`,
      action_taken: t`Action taken`,
      no_action: t`No action`,
      rejected: t`Rejected`,
    }),
    [t],
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>DSA reports</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>
            Digital Services Act content reports — moderation queue with Statement of Reasons (Art.
            16-17).
          </Trans>
        </p>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === s
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {filterLabels[s]} ({counts[s] ?? 0})
          </button>
        ))}
      </div>

      {/* Secondary filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="border-input bg-background h-9 rounded-md border px-2 text-xs"
        >
          <option value="">{t`All categories`}</option>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t`Search reason / email / URL...`}
          className="border-input bg-background h-9 min-w-[260px] flex-1 rounded-md border px-3 text-xs"
        />
      </div>

      {loading ? (
        <Card>
          <CardContent className="text-muted-foreground py-12 text-center text-sm">
            <Trans>Loading...</Trans>
          </CardContent>
        </Card>
      ) : list.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground py-12 text-center text-sm">
            <Trans>No reports match the current filters.</Trans>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <Trans>Reporter</Trans>
                    </TableHead>
                    <TableHead className="hidden md:table-cell">
                      <Trans>Category</Trans>
                    </TableHead>
                    <TableHead className="hidden lg:table-cell">
                      <Trans>Type</Trans>
                    </TableHead>
                    <TableHead className="hidden lg:table-cell">
                      <Trans>Submitted</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Status</Trans>
                    </TableHead>
                    <TableHead className="text-right">
                      <Trans>Actions</Trans>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <div className="text-sm">
                          {r.reporter?.email || r.reporterEmail || (
                            <span className="text-muted-foreground italic">
                              <Trans>(no email)</Trans>
                            </span>
                          )}
                        </div>
                        {r.reporterName && (
                          <div className="text-muted-foreground text-xs">{r.reporterName}</div>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant="outline" className="text-[0.625rem]">
                          {r.category.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <span className="text-muted-foreground text-xs">{r.contentType}</span>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <span className="text-muted-foreground text-xs">
                          {new Date(r.createdAt).toLocaleDateString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-[0.625rem] ${statusStyles[r.status] ?? ""}`}
                        >
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => setOpenId(r.id)}>
                          <Trans>Review</Trans>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {!loading && total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            <Trans>
              Page {page + 1} of {totalPages} — {total} total
            </Trans>
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <Trans>Previous</Trans>
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <Trans>Next</Trans>
            </Button>
          </div>
        </div>
      )}

      {openId && (
        <ReportDetailSheet
          reportId={openId}
          onClose={() => setOpenId(null)}
          onChanged={onResolved}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Detail sheet — full report + resolve / escalate / reject actions
// ─────────────────────────────────────────────────────────────────────────

interface DetailSheetProps {
  reportId: string;
  onClose: () => void;
  onChanged: () => void;
}

function ReportDetailSheet({ reportId, onClose, onChanged }: DetailSheetProps) {
  const { t } = useLingui();
  const { adminFetch } = useAdminFetch();
  const [report, setReport] = useState<DsaReportRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [decision, setDecision] = useState<Decision>("kept");
  const [reasoningText, setReasoningText] = useState("");
  const [notifyReporter, setNotifyReporter] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await adminFetch.get<{ report: DsaReportRow }>(
          `/api/admin/dsa-reports/${reportId}`,
        );
        if (!cancelled) setReport(data.report);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load report");
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [adminFetch, reportId]);

  const submitResolve = useCallback(async () => {
    if (reasoningText.trim().length < 20) {
      setError(t`Statement of Reasons must be at least 20 characters (DSA Art. 17).`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await adminFetch.post(`/api/admin/dsa-reports/${reportId}/resolve`, {
        decision,
        reasoningText: reasoningText.trim(),
        notifyReporter,
      });
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined;
        setError(body?.error ?? `Error ${err.status}`);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Network error");
      }
    }
    setSubmitting(false);
  }, [adminFetch, decision, reasoningText, notifyReporter, reportId, onChanged, t]);

  const submitEscalate = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await adminFetch.post(`/api/admin/dsa-reports/${reportId}/escalate`, {
        note: reasoningText.trim() || undefined,
      });
      onChanged();
    } catch (err) {
      if (err instanceof Error) setError(err.message);
    }
    setSubmitting(false);
  }, [adminFetch, reasoningText, reportId, onChanged]);

  const submitReject = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await adminFetch.patch(`/api/admin/dsa-reports/${reportId}`, { status: "rejected" });
      onChanged();
    } catch (err) {
      if (err instanceof Error) setError(err.message);
    }
    setSubmitting(false);
  }, [adminFetch, reportId, onChanged]);

  const isResolved =
    report?.status === "action_taken" ||
    report?.status === "no_action" ||
    report?.status === "rejected";

  return (
    <Sheet open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <SheetContent className="overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>
            <Trans>DSA report</Trans>
          </SheetTitle>
          <SheetDescription>
            <Trans>Review content notice and record Statement of Reasons.</Trans>
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="text-muted-foreground mt-6 py-12 text-center text-sm">
            <Trans>Loading...</Trans>
          </div>
        ) : !report ? (
          <div className="mt-6 py-12 text-center text-sm text-red-600">
            {error ?? t`Report not found`}
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            {/* Status */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={`text-[0.625rem] ${statusStyles[report.status] ?? ""}`}
              >
                {report.status}
              </Badge>
              <Badge variant="outline" className="text-[0.625rem]">
                {report.category.replace(/_/g, " ")}
              </Badge>
              <span className="text-muted-foreground text-xs">
                {report.contentType} · {new Date(report.createdAt).toLocaleString()}
              </span>
            </div>

            {/* Reason */}
            <section>
              <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                <Trans>Reason</Trans>
              </h3>
              <p className="mt-1 text-sm whitespace-pre-wrap">{report.reason}</p>
            </section>

            {/* Reported content */}
            <section className="bg-muted/40 rounded-md p-3">
              <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                <Trans>Reported content</Trans>
              </h3>
              <dl className="mt-2 space-y-1 text-xs">
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-28 flex-none">
                    <Trans>Type</Trans>
                  </dt>
                  <dd>{report.contentType}</dd>
                </div>
                {report.contentId && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-28 flex-none">
                      <Trans>ID</Trans>
                    </dt>
                    <dd className="font-mono break-all">{report.contentId}</dd>
                  </div>
                )}
                {report.contentUrl && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-28 flex-none">
                      <Trans>URL</Trans>
                    </dt>
                    <dd className="break-all">
                      <a
                        href={report.contentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline"
                      >
                        {report.contentUrl}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </section>

            {/* Reporter */}
            <section className="bg-muted/40 rounded-md p-3">
              <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                <Trans>Reporter</Trans>
              </h3>
              <dl className="mt-2 space-y-1 text-xs">
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-28 flex-none">
                    <Trans>Identity</Trans>
                  </dt>
                  <dd>
                    {report.reporterId ? (
                      <span className="text-emerald-600">
                        <Trans>Authenticated</Trans>
                      </span>
                    ) : (
                      <span className="text-muted-foreground italic">
                        <Trans>Anonymous</Trans>
                      </span>
                    )}
                  </dd>
                </div>
                {(report.reporter?.email || report.reporterEmail) && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-28 flex-none">
                      <Trans>Email</Trans>
                    </dt>
                    <dd>{report.reporter?.email || report.reporterEmail}</dd>
                  </div>
                )}
                {report.reporterName && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-28 flex-none">
                      <Trans>Name</Trans>
                    </dt>
                    <dd>{report.reporterName}</dd>
                  </div>
                )}
                {report.ipAddress && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-28 flex-none">IP</dt>
                    <dd className="font-mono">{report.ipAddress}</dd>
                  </div>
                )}
                {report.userAgent && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-28 flex-none">UA</dt>
                    <dd className="break-all">{report.userAgent}</dd>
                  </div>
                )}
                {report.language && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-28 flex-none">
                      <Trans>Language</Trans>
                    </dt>
                    <dd>{report.language}</dd>
                  </div>
                )}
              </dl>
            </section>

            {/* Existing decision (if resolved) */}
            {isResolved && report.decision && (
              <section className="rounded-md border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-950/30">
                <h3 className="text-xs font-semibold tracking-wide text-emerald-700 uppercase dark:text-emerald-400">
                  <Trans>Statement of Reasons</Trans>
                </h3>
                <p className="mt-1 text-xs whitespace-pre-wrap text-emerald-900 dark:text-emerald-200">
                  {report.decision}
                </p>
                {report.actionTaken && (
                  <p className="mt-2 text-[0.625rem] text-emerald-700 dark:text-emerald-400">
                    <Trans>Action taken: {report.actionTaken}</Trans>
                  </p>
                )}
                {report.decidedAt && (
                  <p className="mt-1 text-[0.625rem] text-emerald-700 dark:text-emerald-400">
                    <Trans>Decided: {new Date(report.decidedAt).toLocaleString()}</Trans>
                  </p>
                )}
                {report.reporterNotifiedAt && (
                  <p className="mt-1 text-[0.625rem] text-emerald-700 dark:text-emerald-400">
                    <Trans>
                      Reporter notified: {new Date(report.reporterNotifiedAt).toLocaleString()}
                    </Trans>
                  </p>
                )}
              </section>
            )}

            {/* Resolution form */}
            {!isResolved && (
              <section className="border-border space-y-3 rounded-md border p-3">
                <h3 className="text-sm font-semibold">
                  <Trans>Record decision</Trans>
                </h3>

                <div className="space-y-1">
                  <label className="text-xs font-medium">
                    <Trans>Decision</Trans>
                  </label>
                  <select
                    value={decision}
                    onChange={(e) => setDecision(e.target.value as Decision)}
                    className="border-input bg-background h-9 w-full rounded-md border px-2 text-xs"
                  >
                    {DECISION_OPTIONS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium">
                    <Trans>Statement of Reasons (DSA Art. 17, min 20 chars)</Trans>
                  </label>
                  <textarea
                    value={reasoningText}
                    onChange={(e) => setReasoningText(e.target.value)}
                    rows={5}
                    placeholder={t`Explain the moderation outcome — sent to the reporter as the official Statement of Reasons.`}
                    className="border-input bg-background w-full rounded-md border px-3 py-2 text-xs"
                  />
                </div>

                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={notifyReporter}
                    onChange={(e) => setNotifyReporter(e.target.checked)}
                    className="size-4"
                  />
                  <Trans>Email Statement of Reasons to reporter</Trans>
                </label>

                {error && <div className="text-xs text-red-600">{error}</div>}

                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" disabled={submitting} onClick={submitResolve}>
                    <Trans>Resolve with SoR</Trans>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={submitting}
                    onClick={submitEscalate}
                  >
                    <Trans>Escalate</Trans>
                  </Button>
                  <Button size="sm" variant="outline" disabled={submitting} onClick={submitReject}>
                    <Trans>Reject (no SoR)</Trans>
                  </Button>
                </div>
              </section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
